import React, { useState, useEffect, useCallback } from 'react';
import { getGalleryByKey } from '../lib/cmsClient';

/**
 * Hjælpefunktion til at hente fuld/stor billed-URL fra et optimeret asset-link.
 * Fjerner eventuel cdn-cgi skæring/lille bredde og henter en skarp high-res variant.
 */
export function getLargeImageUrl(url) {
  if (!url) return '';
  // Hvis URL allerede er fra vores Auxo-asset CDN
  if (url.includes('assets.auxodev.dk')) {
    // Rens eventuel eksisterende cdn-cgi wrapper
    const cleanUrl = url.replace(/https:\/\/assets\.auxodev\.dk\/(cdn-cgi\/image\/[^/]+\/)?/, 'https://assets.auxodev.dk/');
    const path = cleanUrl.replace(/^https:\/\/assets\.auxodev\.dk\//, '');
    return `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/${path}`;
  }
  return url;
}

/**
 * Genanvendelig Lightbox Modal for enkeltbilleder eller billedserier
 */
export function ImageLightboxModal({
  isOpen,
  onClose,
  images = [],
  currentIndex = 0,
  onNavigate,
  title = ''
}) {
  const [activeIdx, setActiveIdx] = useState(currentIndex);

  useEffect(() => {
    setActiveIdx(currentIndex);
  }, [currentIndex]);

  const total = images.length;
  const currentItem = images[activeIdx] || images[0] || null;

  const currentSrc = typeof currentItem === 'string' ? currentItem : currentItem?.src || '';
  const currentAlt = typeof currentItem === 'string' ? (title || 'Arbejdsfoto') : currentItem?.alt || title || 'Arbejdsfoto';
  const currentCaption = typeof currentItem === 'object' ? currentItem?.caption : null;

  const handleNext = useCallback((e) => {
    if (e) e.stopPropagation();
    if (total <= 1) return;
    const nextIdx = (activeIdx + 1) % total;
    setActiveIdx(nextIdx);
    if (onNavigate) onNavigate(nextIdx);
  }, [activeIdx, total, onNavigate]);

  const handlePrev = useCallback((e) => {
    if (e) e.stopPropagation();
    if (total <= 1) return;
    const prevIdx = (activeIdx - 1 + total) % total;
    setActiveIdx(prevIdx);
    if (onNavigate) onNavigate(prevIdx);
  }, [activeIdx, total, onNavigate]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' && total > 1) {
        handleNext();
      } else if (e.key === 'ArrowLeft' && total > 1) {
        handlePrev();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, total, handleNext, handlePrev]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !currentItem) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-stone-950/95 flex flex-col justify-between p-3 sm:p-6 lightbox-overlay backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning i fuld størrelse"
    >
      {/* Top Bar */}
      <div
        className="flex items-center justify-between text-white border-b border-stone-800 pb-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 truncate pr-4">
          <span className="font-heading uppercase tracking-wider font-bold text-xs sm:text-sm text-stone-300 truncate">
            {title || (typeof currentItem === 'object' && currentItem.title) || 'Billedvisning'}
          </span>
          {total > 1 && (
            <span className="text-xs text-[#0072c0] font-mono bg-stone-900 px-2.5 py-0.5 rounded-xs border border-stone-700 shrink-0">
              {activeIdx + 1} / {total}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="text-stone-400 hover:text-white bg-stone-800 hover:bg-stone-700 w-9 h-9 sm:w-10 sm:h-10 rounded-xs flex items-center justify-center transition-colors cursor-pointer shrink-0 focus:outline-none focus:ring-2 focus:ring-[#0072c0]"
          aria-label="Luk billedvisning"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true"></i>
        </button>
      </div>

      {/* Main Image Area */}
      <div className="relative flex-1 flex items-center justify-center py-2 sm:py-4 select-none min-h-0">
        {total > 1 && (
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-1 sm:left-4 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-xs bg-stone-900/80 hover:bg-[#0072c0] text-white flex items-center justify-center transition-colors border border-stone-700 hover:border-[#0072c0] cursor-pointer shadow-xl focus:outline-none focus:ring-2 focus:ring-[#0072c0]"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-base sm:text-lg" aria-hidden="true"></i>
          </button>
        )}

        <div
          className="max-h-[75vh] sm:max-h-[80vh] max-w-full flex flex-col items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={getLargeImageUrl(currentSrc)}
            alt={currentAlt}
            className="max-h-[72vh] sm:max-h-[76vh] max-w-full w-auto object-contain shadow-2xl border border-stone-800 rounded-xs"
          />
          {(currentCaption || (currentAlt && currentAlt !== 'Arbejdsfoto')) && (
            <p className="text-stone-300 text-xs sm:text-sm mt-2 text-center max-w-2xl px-4 line-clamp-2">
              {currentCaption || currentAlt}
            </p>
          )}
        </div>

        {total > 1 && (
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-1 sm:right-4 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-xs bg-stone-900/80 hover:bg-[#0072c0] text-white flex items-center justify-center transition-colors border border-stone-700 hover:border-[#0072c0] cursor-pointer shadow-xl focus:outline-none focus:ring-2 focus:ring-[#0072c0]"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-base sm:text-lg" aria-hidden="true"></i>
          </button>
        )}
      </div>

      {/* Bottom Thumbnails - kun hvis der er flere billeder i serien */}
      {total > 1 && (
        <div
          className="flex items-center justify-center gap-2 overflow-x-auto py-2 border-t border-stone-800"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((item, i) => {
            const thumbSrc = typeof item === 'string' ? item : item.src;
            return (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setActiveIdx(i);
                  if (onNavigate) onNavigate(i);
                }}
                className={`w-12 h-9 sm:w-14 sm:h-11 shrink-0 rounded-xs overflow-hidden border-2 transition-all cursor-pointer ${
                  activeIdx === i
                    ? 'border-[#0072c0] scale-105 opacity-100'
                    : 'border-transparent opacity-50 hover:opacity-80'
                }`}
                aria-label={`Gå til billede ${i + 1}`}
              >
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=150,quality=70,format=auto/${thumbSrc.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Komponent der wrapper et arbejdsbillede med klik-for-større lightbox-trigger
 */
export function ClickableWorkImage({
  src,
  alt,
  className = '',
  style,
  onClick,
  priority = false
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="group relative cursor-pointer block overflow-hidden rounded-xs focus:outline-none focus:ring-2 focus:ring-[#0072c0]"
      aria-label={`Åbn "${alt || 'arbejdsbillede'}" i større visning`}
    >
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading={priority ? 'eager' : 'lazy'}
      />
      {/* Diskret hover-effekt med forstørrelsesglas */}
      <div className="absolute inset-0 bg-stone-950/25 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-200 flex items-center justify-center pointer-events-none">
        <span className="bg-stone-900/80 text-white text-xs px-2.5 py-1.5 rounded-xs flex items-center gap-1.5 shadow-md">
          <i className="fa-solid fa-magnifying-glass-plus text-xs text-[#0072c0]" aria-hidden="true"></i>
          <span className="font-heading font-semibold uppercase text-[10px] tracking-wider">Vis stort</span>
        </span>
      </div>
    </div>
  );
}

/**
 * Standard CMS Galleri Komponent
 */
export default function GalleryLightbox({ galleryKey, title, description, customImages }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadGallery() {
      if (customImages && customImages.length > 0) {
        setGallery({
          title: title || 'Galleri',
          description: description || '',
          images: customImages
        });
        setLoading(false);
        return;
      }

      if (galleryKey) {
        setLoading(true);
        const data = await getGalleryByKey(galleryKey);
        if (isMounted) {
          setGallery(data);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }

    loadGallery();
    return () => {
      isMounted = false;
    };
  }, [galleryKey, customImages, title, description]);

  const images = gallery?.images || [];

  if (loading) {
    return (
      <div className="py-12 text-center text-stone-500">
        <i className="fa-solid fa-circle-notch fa-spin text-2xl mr-2 text-[#0072c0]"></i>
        <span>Indlæser galleri...</span>
      </div>
    );
  }

  if (!gallery || images.length === 0) {
    return null;
  }

  const galleryTitle = title || gallery.title;

  return (
    <section className="my-12">
      <div className="border-t-2 border-stone-300 pt-8 mb-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-stone-900 uppercase font-heading">
              {galleryTitle}
            </h3>
            {(description || gallery.description) && (
              <p className="text-stone-600 mt-1 max-w-2xl text-base">
                {description || gallery.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {images.map((imgSrc, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setActiveIdx(index)}
            className="group relative aspect-4/3 bg-stone-200 overflow-hidden border border-stone-300 text-left focus:outline-none focus:ring-2 focus:ring-[#0072c0] rounded-xs cursor-pointer"
            aria-label={`Åbn billede ${index + 1} i fuld skærm`}
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${imgSrc.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
              alt={`${galleryTitle} - foto ${index + 1}`}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-stone-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
              <span className="bg-stone-900/80 text-white text-xs px-2 py-1 rounded-xs flex items-center gap-1 shadow">
                <i className="fa-solid fa-magnifying-glass-plus text-xs text-[#0072c0]"></i>
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Reusable Modal */}
      <ImageLightboxModal
        isOpen={activeIdx !== null}
        onClose={() => setActiveIdx(null)}
        images={images.map((src, i) => ({
          src,
          alt: `${galleryTitle} - foto ${i + 1}`,
          title: galleryTitle
        }))}
        currentIndex={activeIdx || 0}
        onNavigate={(newIdx) => setActiveIdx(newIdx)}
        title={galleryTitle}
      />
    </section>
  );
}
